import { useEffect, useRef } from "react";

import { Toast, ToastMessage } from "primereact/toast";
import { BehaviorSubject } from "rxjs";

import {
  LocalizedText,
  localized,
  resolveLocalizedText,
  translateError,
  useTranslation,
} from "./shared/language";

const Severity = {
  success: "success",
  info: "info",
  warn: "warn",
  error: "error",
};

const message = new BehaviorSubject<ToastMessage | null>(null);

export const ToastService = {
  Severity,
  setMessage: (data: ToastMessage) => message.next(data),
  getMessage: () => message.asObservable(),
  success: (detail?: unknown) => {
    message.next({
      severity: Severity.success,
      summary: localized("common.success"),
      detail: detail || localized("common.updated"),
    } as ToastMessage);
  },
  error: (detail?: unknown) => {
    message.next({
      severity: Severity.error,
      summary: localized("common.error"),
      detail: detail || localized("common.invalidData"),
    } as ToastMessage);
  },
};

export const AppToast = () => {
  const toast = useRef<Toast>(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    const subscription = ToastService.getMessage().subscribe((toastMessage) => {
      if (toastMessage) {
        toast.current?.show({
          ...toastMessage,
          summary: resolveToastText(toastMessage.summary),
          detail: resolveToastText(toastMessage.detail),
        });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  return <Toast ref={toast} />;
};

function resolveToastText(value: unknown): string {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (isLocalizedText(value)) {
    return resolveLocalizedText(value);
  }

  // Backend messages are already localized through Accept-Language. Extract
  // them without passing them through the frontend translation table.
  return translateError(value);
}

function isLocalizedText(value: unknown): value is LocalizedText {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    (typeof candidate.key === "string" && candidate.key.trim().length > 0) ||
    typeof candidate.text === "string"
  );
}
